import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { getOptimizedImageUrl } from "../../utils/imageOptimization";
import { isVideoUrl } from "../../utils/mediaUtils";

interface Props {
  image: string | null;
  onClose: () => void;
  allImages?: string[];
  currentImageIndex?: number;
}

const ImageLightbox: React.FC<Props> = ({
  image,
  onClose,
  allImages = [],
  currentImageIndex = 0,
}) => {
  const [currentIndex, setCurrentIndex] = useState(currentImageIndex);

  // Al abrir el lightbox, mostrar el medio exacto que el usuario clickeó
  useEffect(() => {
    if (image) {
      const clickedIndex = images.findIndex((img) => img === image);
      setCurrentIndex(clickedIndex !== -1 ? clickedIndex : currentImageIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image]);

  if (!image) return null;

  const hasMultipleImages = allImages.length > 1;
  const images = allImages.length > 0 ? allImages : [image];

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const currentImageToShow = images[currentIndex];
  const currentIsVideo = isVideoUrl(currentImageToShow);

  const lightboxContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      style={{ backdropFilter: "blur(8px)", background: "rgba(0, 0, 0, 0.7)" }}>
      <div className="relative max-w-5xl max-h-[90vh] flex flex-col items-center justify-center">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute -top-12 right-0 text-white hover:text-rose-200 transition-colors p-1"
          title="Cerrar">
          <X className="w-8 h-8" />
        </button>

        {/* Carousel controls fuera de la imagen/video */}
        {hasMultipleImages && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              className="fixed left-4 md:left-8 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white p-3 md:p-4 rounded-full shadow-2xl transition-all border border-white/20 z-50 focus:outline-none"
              title="Anterior">
              <ChevronLeft className="w-7 h-7 md:w-8 md:h-8 text-white" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              className="fixed right-4 md:right-8 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white p-3 md:p-4 rounded-full shadow-2xl transition-all border border-white/20 z-50 focus:outline-none"
              title="Siguiente">
              <ChevronRight className="w-7 h-7 md:w-8 md:h-8 text-white" />
            </button>
          </>
        )}

        <div className="relative flex flex-col items-center justify-center max-w-full max-h-[85vh]">
          {currentIsVideo ? (
            <video
              key={currentImageToShow}
              src={currentImageToShow}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-[80vh] rounded-lg shadow-2xl bg-black"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={getOptimizedImageUrl(currentImageToShow, 1200, 90)}
              alt="Imagen ampliada"
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {/* Indicadores debajo del contenido */}
          {hasMultipleImages && (
            <div className="mt-4 flex gap-2 justify-center items-center">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(i);
                  }}
                  className={`h-2.5 rounded-full transition-all ${
                    i === currentIndex ? "bg-white w-8" : "bg-white/40 hover:bg-white/60 w-2.5"
                  }`}
                  title={`Ver imagen ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(lightboxContent, document.body);
};

export default ImageLightbox;
